<template lang="pug">
study-list.study-selection(
  ref='template',
  :currentLink='currentLink',
  :linkSettings='linkSettings',
  :sideMenuLinkEvent='sideMenuLinkEvent',
  :logoutEvent='logoutEvent',
  :notifications='notifications',
  :notificationInitialLimit='notificationInitialLimit',
  :searchEvent='searchEvent',
  :gradeSelectOptions='gradeSelectOptions',
  :yearSelectOptions='yearSelectOptions',
  :monthSelectOptions='monthSelectOptions',
  :moveSampleEvent='moveSampleEvent',
  :studyList='studyList',
  :listButtonEvent='listButtonEvent',
  :movieUrl='movieUrl',
  :showMoveSampleButton='showMoveSampleButton',
  @yearUpdated='setMonthOptions'
)
  template(#userUidTitle) ID:
  template(#userNameSuffix) さん
  template(#logoutText) ログアウト
  template(#notificationTitle) お知らせ
  template(#studyListTitle) テスト一覧
  template(#searchGradeLabel) 学年
  template(#searchYearLabel) 実施年度
  template(#searchMonthLabel) 実施月
  template(#searchButtonText) 検索
  template(#explanationText) テストの解き方について
  template(#showMovieButtonText) 動画を見る
  template(#moveSampleButtonText) 操作してみる
  template(#subjectHeader) 科目
  template(#sUnitHeader) 選択
  template(#lastMinuteCheckHeader) 直前チェックCBT
  template(#resolveHeader) 解きなおしCBT
  template(#learnedLabel) 実施済み
  template(#notLearnedLabel) はじめる
  template(#hideMovieText) 閉じる
</template>

<script lang="ts">
import { Component, Mixins, Ref } from 'vue-property-decorator'
import LoginMethods from '@/mixins/LoginMethods'
import StudyList from '@/components/templates/student/StudyList.vue'
import { NotificationItem } from '@/models/notificationModel'
import { InputItem } from '@/models/inputItem'
import { StudyItemListType, StudyItem } from '@/models/studyListModel'
import { SideMenuSettings, LinkSetting } from '@/const/SideMenuSettings'
import { NotificationSetting, InputItemPlaceholderSetting } from '@/const/DisplaySettings'
import { SUnitCategorySetting } from '@/const/ApiSettings'
import { StudySelectionUtils } from '@/utils/StudySelectionUtils'
import { StudySearchUtils } from '@/utils/StudySearchUtils'
import MovieApi from '@/mixins/api/MovieApi'
import { ApiResponseMovie } from '@/models/apiResponses/movie'
import NotificationsApi from '@/mixins/api/NotificationsApi'
import { ApiResponseNotificationBoard } from '@/models/apiResponses/notifications'
import StudyContentsApi from '@/mixins/api/StudyContentsApi'
import { ApiResponseExampleStudyContentInfo, ApiResponseStudyContentYear } from '@/models/apiResponses/studyContents'
import GradesApi from '@/mixins/api/GradesApi'
import { ApiResponseGrade } from '@/models/apiResponses/grades'
import StudentStudyContentsApi from '@/mixins/api/student/StudentStudyContentsApi'
import DrillApi from '@/mixins/api/DrillApi'
import OriginalCurriculumsApi from '@/mixins/api/OriginalCurriculumsApi'

/**
 * テスト選択画面
 */
@Component({
  components: {
    StudyList,
  },
})
export default class StudySelection extends Mixins(
  LoginMethods,
  MovieApi,
  NotificationsApi,
  StudyContentsApi,
  GradesApi,
  StudentStudyContentsApi,
  DrillApi,
  OriginalCurriculumsApi
) {
  // テンプレートへのアクセス
  @Ref() template!: StudyList

  // サイドメニューの表示設定
  private currentLink: LinkSetting = SideMenuSettings.STUDY_SELECTION
  private linkSettings: LinkSetting[] = SideMenuSettings.STUDENT_SIDE_MENU

  // お知らせの表示設定
  private notificationInitialLimit = NotificationSetting.INITIAL_LIMIT

  // プルダウンのプレースホルダー設定
  private requiredPlaceholder = StudySearchUtils.buildPlaceholder(InputItemPlaceholderSetting.REQUIRED)

  // お知らせ
  private notifications: NotificationItem[] = []

  // 検索プルダウン
  private gradeSelectOptions: InputItem[] = StudySearchUtils.buildGradeInputItems(this.requiredPlaceholder)
  private yearSelectOptions: InputItem[] = StudySearchUtils.buildYearInputItems(this.requiredPlaceholder)
  private monthSelectOptions: InputItem[] = StudySearchUtils.buildMonthInputItems(this.requiredPlaceholder)

  // テスト一覧
  private studyList: StudyItemListType[] | null = null

  // 解き方動画URL
  private movieUrl = ''

  // サンプル問題小項目ID
  private exampleSUnitId: number | null = null

  // 初期表示自動検索実行中フラグ
  private isProcessingAutomaticSearch = false

  // サンプル問題遷移ボタン表示フラグ
  private get showMoveSampleButton() {
    // サンプル問題小項目IDの有無で判定
    return !!this.exampleSUnitId
  }

  // 生徒画面Cookie情報
  private get studentCookie() {
    return this.$bcbtCookies.studentCookie.get() || {}
  }

  /**
   * [Vue] 画面読み込み時：初期処理
   */
  private async mounted(): Promise<void> {
    // お知らせ情報、学年プルダウン情報、実施年度プルダウン情報、解き方動画情報、サンプル問題情報を取得
    this.$loading.start()
    const apiResponses = await Promise.all([
      this.loadMovieUrl(),
      this.loadNotificationBoard(),
      this.loadGradeOption(),
      this.loadYearOption(),
      this.loadExampleContentInfo(),
    ])
    // 解き方動画URLをセット
    this.movieUrl = StudySelectionUtils.getMovieUrl(apiResponses[0])
    // お知らせをセット
    this.notifications = StudySelectionUtils.formatNotifications(apiResponses[1])
    // 学年プルダウン選択肢をセット
    this.gradeSelectOptions = StudySearchUtils.buildGradeInputItems(this.requiredPlaceholder, apiResponses[2])
    // 実施年度プルダウン選択肢をセット
    this.yearSelectOptions = StudySearchUtils.buildYearInputItems(this.requiredPlaceholder, apiResponses[3])
    // サンプル問題小項目IDをセット
    this.exampleSUnitId = StudySelectionUtils.getExampleSUnitId(apiResponses[4])
    // Cookieに検索条件がセットされている場合は選択値に設定して自動検索
    const studySelectionSearch = this.studentCookie.studySelectionSearch
    if (studySelectionSearch) {
      this.isProcessingAutomaticSearch = true
      this.template.gradeValue = studySelectionSearch.gradeCode
      this.template.yearValue = studySelectionSearch.yearValue
      this.template.monthValue = studySelectionSearch.monthValue
      await this.searchEvent()
    }
    this.$loading.complete()
  }

  /**
   * 動画読み込み
   * @return AWS S3署名付き動画URL
   */
  private async loadMovieUrl(): Promise<ApiResponseMovie | null> {
    return await this.getMovieUrl().catch(() => null)
  }

  /**
   * お知らせ掲示読み込み
   * @return お知らせ掲示一覧
   */
  private async loadNotificationBoard(): Promise<ApiResponseNotificationBoard[] | null> {
    return await this.getApiResponseNotificationBoard().catch(() => null)
  }

  /**
   * 学年情報読み込み
   * @return 学年一覧
   */
  private async loadGradeOption(): Promise<ApiResponseGrade[] | null> {
    return await this.getGrades().catch(() => null)
  }

  /**
   * 実施年度読み込み
   * @return 実施年度一覧
   */
  private async loadYearOption(): Promise<ApiResponseStudyContentYear[] | null> {
    return await this.getYears().catch(() => null)
  }

  /**
   * サンプル問題情報読み込み
   * @returns サンプル問題情報
   */
  private async loadExampleContentInfo(): Promise<ApiResponseExampleStudyContentInfo | null> {
    return await this.getExampleStudyContentInfo().catch(() => null)
  }

  /**
   * 遷移系イベント
   * @param linkSetting サイドメニュー設定
   */
  private sideMenuLinkEvent(linkSetting: LinkSetting) {
    // 現ページのリンク押下時は遷移処理なし
    if (linkSetting === this.currentLink) return

    this.$router.push({ name: `Student${linkSetting.name}` })
  }

  /**
   * サンプル問題の学習ページへ遷移
   */
  private async moveSampleEvent(): Promise<void> {
    // サンプル問題小項目ID未設定時は処理をしない
    if (!this.exampleSUnitId) {
      return
    }

    this.$loading.start()
    try {
      // サンプル問題小項目IDを用いて学習結果IDを取得（初回false、間違い直しfalse）
      const apiResponse = await this.drillStartApi(this.exampleSUnitId, false, false)
      const resultDrillId = StudySelectionUtils.getStartResultDrillId(apiResponse)
      this.$loading.complete()
      // 学習結果IDを付与してテスト画面へ遷移
      this.$router.push({ name: 'StudentStudy', query: { resultDrillId: String(resultDrillId) } })
    } catch (error: any) {
      if (error.response && error.response.status === 400) alert(error.response.data.message)
    }
  }

  /**
   * 学習ボタン押下イベント
   * @param studyItem 科目小項目単位の学習実施状況
   * @param listTitle 一覧のタイトル文字列
   * @param category カテゴリー（直前チェックCBT/解きなおしCBT）
   */
  private listButtonEvent(studyItem: StudyItem, listTitle: string, category: SUnitCategorySetting) {
    switch (category) {
      case SUnitCategorySetting.LAST_MINUTE_CHECK:
        // 直前チェック（直前チェック小項目IDが設定されている場合のみ遷移）
        if (studyItem.lastMinuteCheckSUnitId) {
          this.$router.push({
            name: 'StudentStudyTop',
            query: {
              sUnitId: String(studyItem.lastMinuteCheckSUnitId),
              isLearned: String(studyItem.isLastMinuteCheckLearned),
            },
          })
        }
        break
      case SUnitCategorySetting.RESOLVE:
        // 解きなおし（解きなおしCSV存在フラグが立っていて、解きなおし小項目IDが設定されている場合のみ遷移）
        if (studyItem.isResolveCsvExist && studyItem.resolveSUnitId) {
          this.$router.push({
            name: 'StudentStudyTop',
            query: {
              sUnitId: String(studyItem.resolveSUnitId),
              isLearned: String(studyItem.isResolveLearned),
            },
          })
        }
        break
    }
  }

  /**
   * ログアウト
   */
  private logoutEvent(): void {
    this.logout()
    // ログインページへ遷移
    this.$router.push({ name: 'StudentLogin' })
  }

  /**
   * 検索
   */
  private async searchEvent(): Promise<void> {
    // プルダウン選択した学年、実施年度、実施月を用いて検索処理
    this.$loading.start()
    const apiResponse = await this.getStudentStudyContents(
      Number(this.template.yearValue),
      Number(this.template.monthValue),
      Number(this.template.gradeValue)
    )
    // studyListに値をセット
    this.studyList = StudySelectionUtils.formatStudyList(apiResponse)
    // Cookieに検索条件をセット
    const studentCookie = this.studentCookie
    studentCookie.studySelectionSearch = {
      gradeCode: this.template.gradeValue,
      yearValue: this.template.yearValue,
      monthValue: this.template.monthValue,
    }
    await this.$bcbtCookies.studentCookie.set(studentCookie)
    this.$loading.complete()
  }

  /**
   * 実施年度プルダウン切り替え時、実施月プルダウンを設定
   */
  private async setMonthOptions() {
    this.$loading.start()
    // 実施年度未選択状態の場合、プルダウンは空になる
    const apiResponse = this.template.yearValue ? await this.getMonths(Number(this.template.yearValue)) : null
    this.monthSelectOptions = StudySearchUtils.buildMonthInputItems(this.requiredPlaceholder, apiResponse)
    // 初期表示自動検索時以外は実施月を未選択状態に戻す
    if (!this.isProcessingAutomaticSearch) {
      this.template.monthValue = ''
    }
    this.isProcessingAutomaticSearch = false
    this.$loading.complete()
  }
}
</script>
